import React, { useState, useEffect, useRef } from 'react';
import './Dashboard.css';
import { apiRequest, parseResponse } from './utils/api';
import RidesPage from './RidesPage';
import ContactUs from './ContactUs';
import FaqHelp from './FaqHelp';
import ProfilePage from './ProfilePage';
import { useLanguage } from './i18n/LanguageContext';

const Dashboard = ({ onLocationSelect, onNavigateToRides, onRideSelect, isLoggedIn, onRequireLogin, onChangeHotel, initialTab = 'HOME', onTabChange }) => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' (vertical) or 'grid' (horizontal)
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab || 'HOME');
  }, [initialTab]);

  const goToTab = (tab) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [recommendedPlaces, setRecommendedPlaces] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [placesError, setPlacesError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const categoryListRef = useRef(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [showFaq, setShowFaq] = useState(false);
  const [faqItem, setFaqItem] = useState(null);

  // Get user name from localStorage
  const guestData = JSON.parse(localStorage.getItem('guestData') || '{}');
  const userName = (isLoggedIn && guestData.fullName) ? guestData.fullName.split(' ')[0] : 'Guest';

  // Get user location from localStorage (stored during hotel confirmation)
  const selectedHotel = JSON.parse(localStorage.getItem('selectedHotel') || '{}');
  const userLatitude = selectedHotel.latitude || null;
  const userLongitude = selectedHotel.longitude || null;

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        setCategoriesError(null);

        const categoryQuery = userLatitude != null && userLongitude != null
          ? `?latitude=${userLatitude}&longitude=${userLongitude}`
          : '';
        const response = await apiRequest(`/categories${categoryQuery}`, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch categories');
        }

        const data = await parseResponse(response);
        const categoriesArray = Array.isArray(data) ? data : [];

        // Add 'All' category at the beginning
        const updatedCategories = [{ id: 'all', name: 'All' }, ...categoriesArray];

        if (updatedCategories.length > 0) {
          setCategories(updatedCategories);
          // Set 'All' category as selected by default
          setSelectedCategory('all');
        } else {
          setCategoriesError('No categories found');
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategoriesError('Unable to load categories. Please try again.');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [userLatitude, userLongitude]);

  useEffect(() => {
    const list = categoryListRef.current;
    if (!list || selectedCategory == null) return;
    const active = list.querySelector('.category-button.active');
    if (!active) return;
    const listRect = list.getBoundingClientRect();
    const itemRect = active.getBoundingClientRect();
    const nextLeft = list.scrollLeft + (itemRect.left - listRect.left) - (listRect.width / 2) + (itemRect.width / 2);
    list.scrollTo({ left: Math.max(0, nextLeft), behavior: 'smooth' });
  }, [selectedCategory, categories]);

  // Fetch popular locations when category is selected
  useEffect(() => {
    if (selectedCategory && userLatitude && userLongitude) {
      const fetchPopularLocations = async () => {
        try {
          setLoadingPlaces(true);
          setPlacesError(null);

          // Get category ID (handle different response formats)
          const categoryId = typeof selectedCategory === 'object'
            ? selectedCategory.id || selectedCategory.categoryId
            : selectedCategory;

          // If 'all' is selected, fetch all locations, otherwise fetch nearby by category
          const endpoint = categoryId === 'all'
            ? `/addPopularLocations/nearby?latitude=${userLatitude}&longitude=${userLongitude}`
            : `/addPopularLocations/nearby?latitude=${userLatitude}&longitude=${userLongitude}&categoryId=${categoryId}`;

          const response = await apiRequest(endpoint, {
            method: 'GET'
          });

          if (!response.ok) {
            throw new Error('Failed to fetch popular locations');
          }

          const data = await parseResponse(response);

          // Handle both direct array responses and paginated objects
          const locationsArray = Array.isArray(data)
            ? data
            : (data.content && Array.isArray(data.content))
              ? data.content
              : [];

          if (locationsArray.length > 0) {
            setRecommendedPlaces(locationsArray);
          } else {
            setRecommendedPlaces([]);
            setPlacesError('No locations found for this category');
          }
        } catch (err) {
          console.error('Error fetching popular locations:', err);
          setPlacesError('Unable to load locations. Please try again.');
          setRecommendedPlaces([]);
        } finally {
          setLoadingPlaces(false);
        }
      };

      fetchPopularLocations();
    }
  }, [selectedCategory, userLatitude, userLongitude]);


  const handleLocationClick = (location) => {
    // Normalize location data to ensure correct format
    const normalizedLocation = {
      id: location.id,
      locationName: location.locationName || location.name || 'Unknown Location',
      description: location.description || '',
      latitude: location.latitude,
      longitude: location.longitude,
      photo: location.photo || location.imageUrl || null,
      type: location.type || null,
      categoryId: location.categoryId || null
    };
    // Pass location selection to parent (App.js)
    if (onLocationSelect) {
      onLocationSelect(normalizedLocation);
    }
  };

  const toggleDescription = (placeId, e) => {
    e.stopPropagation(); // Prevent card click
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(placeId)) {
        newSet.delete(placeId);
      } else {
        newSet.add(placeId);
      }
      return newSet;
    });
  };

  // Search functionality with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout if it exists
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // If query is empty, clear results immediately
    if (query.trim().length === 0) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    // Show search results container but not loading state yet
    setShowSearchResults(true);
    setIsSearching(false);

    // Debounce search API call - only call API after user stops typing for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
      searchTimeoutRef.current = null;
    }, 500); // Wait 500ms after user stops typing
  };

  const performSearch = async (query) => {
    if (!query || query.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const encodedQuery = encodeURIComponent(query.trim());
      const response = await apiRequest(
        `/search?name=${encodedQuery}&page=0&size=5`,
        {
          method: 'GET'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to search');
      }

      const data = await parseResponse(response);

      // Extract content array from response
      const results = data.content || [];
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchResultClick = (result) => {
    // Convert search result to location format expected by ConfirmTrip
    const location = {
      id: result.id,
      locationName: result.name,
      description: result.description,
      latitude: result.latitude,
      longitude: result.longitude,
      photo: result.imageUrl,
      type: result.type
    };

    // Clear search
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);

    // Navigate to confirm trip
    handleLocationClick(location);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container') && !event.target.closest('.search-results')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Bottom Navigation tab switching logic
  const handleTabChange = (tab) => {
    if ((tab === 'RIDE' || tab === 'PROFILE') && !isLoggedIn) {
      if (onRequireLogin) {
        onRequireLogin();
      }
      return;
    }
    goToTab(tab);
  };

  // Get hotel name for location indicator
  const hotelName = selectedHotel.hotelName || 'Hotel';

  return (
    <div className={`dashboard${activeTab === 'HELP' || activeTab === 'CONTACT' || showFaq ? ' is-contact' : ''}`}>
      {/* Purple Gradient Header - only show on Home, including while the profile drawer is open */}
      {(activeTab === 'HOME' || activeTab === 'PROFILE') && (
        <header className="dashboard-header-gradient">
          <div className="book-premim-car">
            <h2 className="premium-banner-title">{t('Book Premium Rides for now or later.')}</h2>
            <img
              className="premium-taxi"
              src={`${process.env.PUBLIC_URL || ''}/images/dashboard-taxi.png`}
              alt=""
            />
          </div>
          <div className='text-contant'>


            <div className="header-top">
              {/* Menu button removed */}
            </div>

            {/* Location Indicator */}
            <button type="button" className="location-indicator" onClick={onChangeHotel}>
              <span className="location-text">
                <svg className="hotel-pin-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
                </svg>
                <p className="hotelname">{hotelName}</p>
              </span>
              <svg className="chevron-down" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Greeting Section */}
            <div className="greeting-section">
              <h1 className="greeting">
                {t('Hi {name}!', { name: '\u0000' }).split('\u0000')[0]}
                <span className="greeting-name">{userName}</span>
                {t('Hi {name}!', { name: '\u0000' }).split('\u0000')[1]}
              </h1>
              <p className="greeting-subtitle">{t('Where would you like to go today?')}</p>
            </div>

            {/* Search Bar */}
            <div className={`search-container ${isSearching ? 'searching' : ''} ${showSearchResults ? 'has-results' : ''}`}>
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('Search New Delhi...')}
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchQuery.length > 0 || searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {isSearching && (
                <div className="search-loading">
                  <div className="search-spinner"></div>
                </div>
              )}

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="search-results">
                  {searchResults.length > 0 ? (
                    <div className="search-results-list">
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          className="search-result-item"
                          onClick={() => handleSearchResultClick(result)}
                        >
                          <div className="search-result-image">
                            {result.imageUrl ? (
                              <img
                                src={result.imageUrl}
                                alt={result.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="search-result-image-fallback" style={{ display: result.imageUrl ? 'none' : 'flex' }}>
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" />
                                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            </div>
                          </div>
                          <div className="search-result-content">
                            <h4 className="search-result-name">{result.name}</h4>
                            {result.description && (
                              <p className="search-result-description">{result.description}</p>
                            )}
                            {result.type && (
                              <span className="search-result-type">{result.type}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : searchQuery.length > 0 && !isSearching ? (
                    <div className="search-no-results">
                      <p>{t('No results found')}</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      <div className="dashboard-content">
        {/* Show Rides Page when Ride tab is active */}
        {activeTab === 'RIDE' ? (
          <RidesPage
            onRideSelect={onRideSelect}
            onBack={() => goToTab('HOME')}
            onNavigateTab={(tab) => {
              if (tab === 'PROFILE' && !isLoggedIn) {
                onRequireLogin();
                return;
              }
              goToTab(tab);
            }}
          />
        ) : showFaq ? (
          <FaqHelp
            initialAnswer={faqItem}
            onBack={() => {
              setShowFaq(false);
              setFaqItem(null);
            }}
          />
        ) : activeTab === 'HELP' || activeTab === 'CONTACT' ? (
          <div className="contact-tab-content">
            <ContactUs
              onBack={() => goToTab('HOME')}
              onOpenFaq={(item) => {
                setFaqItem(item || null);
                setShowFaq(true);
              }}
            />
          </div>
        ) : (
          <>
            {/* Service Categories with View Toggle */}
            <div className="service-categories-section">
              <div className="categories-header">
                {loadingCategories ? (
                  <div className="categories-loading">
                    <p>{t('Loading categories...')}</p>
                  </div>
                ) : categoriesError ? (
                  <div className="categories-error">
                    <p>{categoriesError}</p>
                  </div>
                ) : (
                  <div className="categories-container" ref={categoryListRef}>
                    {categories.map((category) => {
                      // Handle different API response formats
                      const categoryId = category.id || category.categoryId || category;
                      const categoryName = category.name || category.title || category;
                      const isSelected = selectedCategory === categoryId;

                      return (
                        <button
                          key={categoryId}
                          className={`category-button ${isSelected ? 'active' : ''}`}
                          onClick={() => setSelectedCategory(categoryId)}
                        >
                          {categoryId === 'all' ? t('All') : categoryName}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="view-toggle">
                  <button
                    className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    aria-label={t('List view')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button
                    className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    aria-label={t('Grid view')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" />
                      <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" />
                      <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" />
                      <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Recommended Section */}
            <div className="recommended-section">

              {loadingPlaces ? (
                <div className="places-loading">
                  <p>{t('Loading locations...')}</p>
                </div>
              ) : placesError ? (
                <div className="places-error">
                  <p>{placesError}</p>
                </div>
              ) : recommendedPlaces.length > 0 ? (
                <div className={`recommended-cards ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                  {recommendedPlaces.map((place) => {
                    // Handle API response format
                    const placeId = place.id;
                    const placeName = (place.locationName || place.name || 'Unknown Location').trim();
                    const placeDescription = place.description || '';
                    const placeImage = place.photo || place.imageUrl || null;

                    // Get category name for tag
                    const selectedCategoryObj = categories.find(cat => {
                      const catId = cat.id || cat.categoryId || cat;
                      return catId === selectedCategory;
                    });
                    const categoryName = selectedCategoryObj?.name || selectedCategoryObj?.title || 'AIRPORT TRANSFER';

                    return (
                      <div
                        key={placeId}
                        className={`recommended-card ${viewMode === 'list' ? 'list-card' : 'grid-card'}`}
                        onClick={() => handleLocationClick(place)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={`card-image-placeholder ${viewMode === 'list' ? 'list-image' : 'grid-image'}`}>
                          {placeImage ? (
                            <img
                              src={placeImage}
                              alt={placeName}
                              className="card-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="card-image-fallback" style={{ display: placeImage ? 'none' : 'flex' }}>
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" />
                              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </div>
                        </div>
                        <div className={`card-content ${viewMode === 'list' ? 'list-content' : 'grid-content'}`}>
                          <div className="card-info">
                            <h3 className="card-title">{placeName}</h3>
                            {placeDescription && (
                              <div className="card-description-wrapper">
                                <p className={`card-subtitle ${expandedDescriptions.has(placeId) ? 'expanded' : 'truncated'}`}>
                                  {placeDescription}
                                </p>
                                {placeDescription.length > 80 && (
                                  <button
                                    className="description-toggle"
                                    onClick={(e) => toggleDescription(placeId, e)}
                                  >
                                    {expandedDescriptions.has(placeId) ? t('less') : t('more')}
                                  </button>
                                )}
                              </div>
                            )}
                            {viewMode === 'list' && (
                              <span className="card-category-tag">{categoryName.toUpperCase()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="places-empty">
                  <p>{t('No locations available. Please select a category.')}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {activeTab === 'PROFILE' && (
        <ProfilePage onBack={() => goToTab('HOME')} onRequireLogin={onRequireLogin} />
      )}

      {/* Bottom Navigation. The rides screen draws its own bar. */}
      {activeTab !== 'RIDE' && (
      <nav className="bottom-navigation">
        <button
          className={`nav-item ${activeTab === 'HOME' ? 'active' : ''}`}
          onClick={() => handleTabChange('HOME')}
        >
          <svg width="27" height="24" viewBox="0 0 27 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24.6172 11.5303H23.5068C22.6785 11.5304 22.0068 12.2019 22.0068 13.0303V22.8242H17.0068V15.8535C17.0068 15.0251 16.3352 14.3535 15.5068 14.3535H10.9072C10.0788 14.3535 9.40729 15.0251 9.40723 15.8535V22.8242H4.40723V13.0303C4.40723 12.2019 3.73556 11.5304 2.90723 11.5303H1.79688L13.207 1.1748L24.6172 11.5303Z" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>{t('Home')}</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'RIDE' ? 'active' : ''}`}
          onClick={() => handleTabChange('RIDE')}
        >
          <svg width="32" height="17" viewBox="0 0 32 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M26.5 15.9995H29.5C30.4 15.9995 31 15.3995 31 14.4996V9.99971C31 8.64975 29.95 7.44979 28.75 7.1498C26.05 6.39982 22 5.49985 22 5.49985C22 5.49985 20.05 3.39992 18.7 2.04997C17.95 1.44999 17.05 1 16 1H5.5C4.6 1 3.85 1.59998 3.4 2.34996L1.3 6.69981C1.10137 7.27913 1 7.88733 1 8.49976V14.4996C1 15.3995 1.6 15.9995 2.5 15.9995H5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('Ride')}</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'HELP' || activeTab === 'CONTACT' ? 'active' : ''}`}
          onClick={() => handleTabChange('HELP')}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('Contact')}</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'PROFILE' ? 'active' : ''}`}
          onClick={() => handleTabChange('PROFILE')}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>{t('Profile')}</span>
        </button>
      </nav>
      )}
    </div>
  );
};

export default Dashboard;
